import { PaymentAccountTypeSelect } from "@components/inputs/PaymentAccountTypeSelect";
import RHFController from "@components/inputs/RHFController";
import MemberSettingsRenderer from "@components/utils/MemberSettingsRenderer";
import useContactLinks from "@hooks/useContactLinks";
import { useFundingProfileFormConfig } from "@hooks/useFundingProfileFormConfig";
import { useMemberSettings } from "@hooks/useMemberSettings";
import {
  Checkbox,
  cn,
  FundingProfileType,
  MemberSettingFundingMode,
  MemberSettingNameMode,
  Select,
  TextField,
} from "kz-ui-sdk";
import { useCallback, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { EWalletFormConfig, InnerFormProps } from "../../types";
import InlineErrorWithContact from "../InlineErrorWithContact";

const fundingMode = MemberSettingFundingMode.EWalletOnly;

const EWalletInnerForm = ({
  errors,
  control,
  isLoading,
  displayLegalName,
  allowEditDefault,
  fieldHelperTextManager,
}: InnerFormProps) => {
  const { t } = useTranslation();
  const { phoneMinLength, phoneMaxLength, phoneStartWith } = useMemberSettings();
  const { openContact } = useContactLinks();
  const { eWalletFormConfig = {} } = useFundingProfileFormConfig();

  const pidType = useWatch({ control, name: "pidType" });

  const handleOpenContactLink = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      openContact();
    },
    [openContact],
  );

  // Helper to get the actual error message key (strip "backend:" prefix if present)
  const getErrorMessageKey = (errorMessage?: string): string | undefined => {
    if (!errorMessage) return undefined;
    return errorMessage.startsWith("backend:") ? errorMessage.slice(8) : errorMessage;
  };

  // Check if accountNo error requires contact CS
  const accountNoErrorKey = getErrorMessageKey(errors.accountNo?.message);
  const showLinkedError = accountNoErrorKey === t(`${fundingMode} is linked to another user.`);

  // Check if unique identifier errors require contact CS (errors ending with "is not valid")
  const showPanError = !!errors.pan?.message?.endsWith("is not valid");
  const showCpfError = !!errors.cpf?.message?.endsWith("is not valid");
  const showCurpError = !!errors.curp?.message?.endsWith("is not valid");
  const showPidError = !!errors.pidNumber?.message?.endsWith("is not valid");

  const accountNoPlaceholder = useMemo(() => {
    // start with phoneStartWith, fill the rest with 01234567890123
    const fillLength = phoneMinLength - phoneStartWith.length;
    let fillStr = "";
    for (let i = 1; i <= fillLength; i++) {
      fillStr += String(i % 10);
    }
    return `${phoneStartWith}${fillStr}`;
  }, [phoneMinLength, phoneStartWith]);

  const sortedDynamicKeys = useMemo(() => {
    const candidateKeys = Object.keys(eWalletFormConfig) as (keyof Omit<EWalletFormConfig, never>)[];
    const enabledKeys = candidateKeys.filter((k) => eWalletFormConfig[k]?.enabled !== false);
    return enabledKeys.sort((a, b) => {
      const pa = eWalletFormConfig[a]?.sortPriority ?? 9999;
      const pb = eWalletFormConfig[b]?.sortPriority ?? 9999;
      return pa - pb;
    });
  }, [eWalletFormConfig]);

  const renderDynamicFields = useCallback(() => {
    const rendered = new Set<string>();
    return sortedDynamicKeys.map((key) => {
      if (rendered.has(key as string)) return null;
      switch (key) {
        case "fullName": {
          if (!displayLegalName || eWalletFormConfig?.fullName?.enabled === false) return null;
          return (
            <MemberSettingsRenderer
              key={key as string}
              feature="nameMode"
              match={MemberSettingNameMode.FullInFirst}
            >
              <label className="flex flex-col">
                <span
                  className={cn("mb-0.5 text-sm text-content-base", {
                    "text-error-base": fieldHelperTextManager?.shouldShowError("accountName"),
                  })}
                >
                  {t("full name")}
                </span>
                <RHFController
                  control={control}
                  name="accountName"
                  fieldHelperTextManager={fieldHelperTextManager}
                >
                  <TextField
                    placeholder={"John Doe"}
                    disabled={isLoading}
                    aria-label="E-wallet full name input"
                  />
                </RHFController>
              </label>
            </MemberSettingsRenderer>
          );
        }
        case "firstName":
        case "lastName": {
          if (!displayLegalName) return null;
          if (rendered.has("firstName") || rendered.has("lastName")) return null;
          rendered.add("firstName");
          rendered.add("lastName");
          return (
            <MemberSettingsRenderer
              key="firstLast"
              feature="nameMode"
              match={MemberSettingNameMode.FirstLast}
            >
              <div className="grid grid-cols-2 gap-4">
                {eWalletFormConfig?.firstName?.enabled !== false && (
                  <label className="flex flex-col">
                    <span
                      className={cn("mb-0.5 text-sm text-content-base", {
                        "text-error-base": fieldHelperTextManager?.shouldShowError("firstName"),
                      })}
                    >
                      {t("first name")}
                    </span>
                    <RHFController
                      control={control}
                      name="firstName"
                      fieldHelperTextManager={fieldHelperTextManager}
                    >
                      <TextField
                        placeholder={"John"}
                        disabled={isLoading}
                        aria-label="E-wallet first name input"
                      />
                    </RHFController>
                  </label>
                )}
                {eWalletFormConfig?.lastName?.enabled !== false && (
                  <label className="flex flex-col">
                    <span
                      className={cn("mb-0.5 text-sm text-content-base", {
                        "text-error-base": fieldHelperTextManager?.shouldShowError("lastName"),
                      })}
                    >
                      {t("last name")}
                    </span>
                    <RHFController
                      control={control}
                      name="lastName"
                      fieldHelperTextManager={fieldHelperTextManager}
                    >
                      <TextField
                        placeholder={"Doe"}
                        disabled={isLoading}
                        aria-label="E-wallet last name input"
                      />
                    </RHFController>
                  </label>
                )}
              </div>
            </MemberSettingsRenderer>
          );
        }
        case "pan": {
          if (!eWalletFormConfig?.pan?.enabled) return null;
          return (
            <div key={key as string}>
              <label className="flex flex-col">
                <span
                  className={cn("mb-0.5 text-sm text-content-base", {
                    "text-error-base": fieldHelperTextManager?.shouldShowError("pan"),
                  })}
                >
                  {t("pan number")}
                </span>
                <RHFController
                  control={control}
                  name="pan"
                  hideErrorText={showPanError}
                  fieldHelperTextManager={fieldHelperTextManager}
                >
                  <TextField
                    placeholder={"AFZPK7190K"}
                    disabled={isLoading}
                    classes={{
                      "input&": "uppercase",
                    }}
                    aria-label="E-wallet pan input"
                  />
                </RHFController>
              </label>
              <InlineErrorWithContact
                show={showPanError}
                onContact={handleOpenContactLink}
                messageKey={errors.pan?.message}
              />
            </div>
          );
        }
        case "cpf": {
          if (!eWalletFormConfig?.cpf?.enabled) return null;
          return (
            <div key={key as string}>
              <label className="flex flex-col">
                <span
                  className={cn("mb-0.5 text-sm text-content-base", {
                    "text-error-base": fieldHelperTextManager?.shouldShowError("cpf"),
                  })}
                >
                  {t("cpf number")}
                </span>
                <RHFController
                  control={control}
                  name="cpf"
                  hideErrorText={showCpfError}
                  fieldHelperTextManager={fieldHelperTextManager}
                >
                  <TextField
                    placeholder={"231.002.999-00"}
                    disabled={isLoading}
                    aria-label="E-wallet cpf number input"
                  />
                </RHFController>
              </label>
              <InlineErrorWithContact
                show={showCpfError}
                onContact={handleOpenContactLink}
                messageKey={errors.cpf?.message}
              />
            </div>
          );
        }
        case "curp": {
          if (!eWalletFormConfig?.curp?.enabled) return null;
          return (
            <div key={key as string}>
              <label className="flex flex-col">
                <span
                  className={cn("mb-0.5 text-sm text-content-base", {
                    "text-error-base": fieldHelperTextManager?.shouldShowError("curp"),
                  })}
                >
                  {t("curp number")}
                </span>
                <RHFController
                  control={control}
                  name="curp"
                  hideErrorText={showCurpError}
                  fieldHelperTextManager={fieldHelperTextManager}
                >
                  <TextField
                    placeholder={"HEGG560427MVZRRL04"}
                    disabled={isLoading}
                    aria-label="E-wallet curp number input"
                  />
                </RHFController>
              </label>
              <InlineErrorWithContact
                show={showCurpError}
                onContact={handleOpenContactLink}
                messageKey={errors.curp?.message}
              />
            </div>
          );
        }
        case "pid": {
          if (!eWalletFormConfig?.pid?.enabled) return null;

          const getPlaceholder = () => {
            switch (pidType) {
              case "dni":
                return "12345678";
              case "ce":
                return "A12345678";
              case "pen_pas":
                return "A12345678";
              case "ruc":
                return "12345678901";
              case "cc":
                return "1234567890";
              case "nit":
                return "123456789012";
              case "cop_pas":
                return "P1234567";
              default:
                return "";
            }
          };

          const getLabel = () => {
            switch (pidType) {
              case "dni":
                return `${t("pid number")}: ${t("dni")}`;
              case "ce":
                return `${t("pid number")}: ${t("ce")}`;
              case "pen_pas":
                return `${t("pid number")}: ${t("pen_pas")}`;
              case "ruc":
                return `${t("pid number")}: ${t("ruc")}`;
              case "cc":
                return `${t("pid number")}: ${t("cc")}`;
              case "nit":
                return `${t("pid number")}: ${t("nit")}`;
              case "cop_pas":
                return `${t("pid number")}: ${t("cop_pas")}`;
              default:
                return t("pid number");
            }
          };

          // Use pre-computed PID type options from config
          const pidTypeOptions = (eWalletFormConfig?.pid?.pidTypeOptions || []).map((option) => ({
            id: option.id,
            name: t(option.name),
          }));

          return (
            <div
              key={key as string}
              className="flex flex-col gap-4"
            >
              <label className="flex flex-col">
                <span className={cn("mb-0.5 text-sm text-content-base", { "text-error-base": errors.pidType })}>
                  {t("pid type")}
                </span>
                <RHFController
                  control={control}
                  name="pidType"
                >
                  <Select
                    placeholder={t("please select")}
                    options={pidTypeOptions}
                    size={"lg"}
                    classes={{
                      "root&": "gap-0",
                      "label&": "text-content-base text-base",
                      "placeHolder&": cn("text-content-soft text-start w-56", { "text-content-base": !!pidType }),
                      "helperText&": "!mt-1 !text-xs my-0 px-0",
                      "selectedOption&": "!text-content-base text-base",
                      "errorText&": "text-error-base text-xs",
                      "option&": "cursor-pointer hover:bg-white hover:bg-opacity-5",
                      "optionsContainer&": "divide-stroke-base divide-y",
                    }}
                    aria-label="E-wallet pid type select"
                  />
                </RHFController>
              </label>
              {pidType && (
                <label className="flex flex-col">
                  <span
                    className={cn("mb-0.5 text-sm text-content-base", {
                      "text-error-base": fieldHelperTextManager?.shouldShowError("pidNumber"),
                    })}
                  >
                    {getLabel()}
                  </span>
                  <RHFController
                    control={control}
                    name="pidNumber"
                    hideErrorText={showPidError}
                    fieldHelperTextManager={fieldHelperTextManager}
                  >
                    <TextField
                      placeholder={getPlaceholder()}
                      disabled={isLoading}
                      classes={{
                        "input&": cn({
                          uppercase: pidType === "ce" || pidType === "pen_pas" || pidType === "cop_pas",
                        }),
                      }}
                      aria-label="E-wallet pid number input"
                    />
                  </RHFController>
                  <InlineErrorWithContact
                    show={showPidError}
                    onContact={handleOpenContactLink}
                    messageKey={errors.pidNumber?.message}
                  />
                </label>
              )}
            </div>
          );
        }
        case "accountNumber": {
          // Account number is always shown, but we can check config if needed
          return null; // Handled separately below
        }
        default:
          return null;
      }
    });
  }, [
    sortedDynamicKeys,
    eWalletFormConfig,
    displayLegalName,
    errors,
    control,
    isLoading,
    t,
    pidType,
    handleOpenContactLink,
    showPanError,
    showCpfError,
    showCurpError,
    showPidError,
    fieldHelperTextManager,
  ]);

  return (
    <>
      {/* DYNAMIC FIELDS (name, pan, cpf, curp, pid) */}
      {renderDynamicFields()}

      {/* E-WALLET SELECT */}
      <label className="flex flex-col">
        <span className={cn("mb-0.5 text-sm text-content-base", { "text-error-base": errors.accountType })}>
          {t("e-wallet type")}
        </span>
        <RHFController
          control={control}
          name="accountType"
        >
          <PaymentAccountTypeSelect
            disabled={isLoading}
            accountType={FundingProfileType.EWallet}
          />
        </RHFController>
      </label>

      {/* E-WALLET NUMBER */}
      <div>
        <label className="flex flex-col">
          <span
            className={cn("mb-0.5 text-sm text-content-base", {
              "text-error-base": fieldHelperTextManager?.shouldShowError("accountNo"),
            })}
          >
            {t("e-wallet number")}
          </span>
          <RHFController
            control={control}
            name="accountNo"
            hideErrorText={showLinkedError}
            helperTextParams={{
              minLength: phoneMinLength,
              maxLength: phoneMaxLength,
              startWith: Array.isArray(phoneStartWith) ? phoneStartWith.join(", ") : phoneStartWith,
              fundingMode: fundingMode || "",
            }}
            fieldHelperTextManager={fieldHelperTextManager}
          >
            <TextField
              placeholder={accountNoPlaceholder}
              disabled={isLoading}
            />
          </RHFController>
        </label>
        {showLinkedError && (
          <p className="text-right text-xs leading-4 text-error-base transition-[max-height]">
            {t(`${fundingMode} is linked to another user.`)}{" "}
            <button
              onClick={handleOpenContactLink}
              className="cursor-pointer underline"
            >
              {t("contact cs")}
            </button>
          </p>
        )}
      </div>

      {/* DEFAULT CHECKBOX */}
      <div>
        <label className="inline-flex items-center justify-start gap-[2px]">
          <RHFController
            control={control}
            name="isDefault"
          >
            <Checkbox
              variant="outlined"
              classes={{
                input_outlined: "checked:border-accentBrand-500 checked:bg-accentBrand-500 border-stroke-base border-2",
                "root&": !allowEditDefault ? "opacity-30" : "",
              }}
              disabled={!allowEditDefault}
            />
          </RHFController>

          <p className="text-sm text-content-base">{t("set as default")}</p>
        </label>
      </div>
    </>
  );
};

export default EWalletInnerForm;
